import { ErrorWithDetails } from '@app-velocity/common/errors';

import { OriginalClonePair } from '../../types';

const formatFontList = (fonts: FontName[]) =>
  fonts.map((font) => `${font.family} ${font.style}`).join(', ');

export const loadMissingFontsFromPairs = async (
  textNodes: OriginalClonePair<TextNode>[]
) => {
  const fontsToLoad: FontName[] = [];
  const addedFonts = new Set<string>();

  for (const node of textNodes) {
    const fonts = node.clone.getStyledTextSegments(['fontName']);

    for (const nextFont of fonts) {
      const fontShortcut = `${nextFont.fontName.family}_${nextFont.fontName.style}`;
      if (addedFonts.has(fontShortcut)) continue;
      addedFonts.add(fontShortcut);

      fontsToLoad.push(nextFont.fontName);
    }
  }

  await loadMissingFonts(fontsToLoad);
};

export const loadMissingFontsFromTextNodes = async (textNodes: TextNode[]) => {
  const fontsToLoad: FontName[] = [];
  const addedFonts = new Set<string>();

  for (const node of textNodes) {
    const fonts = node.getStyledTextSegments(['fontName']);

    for (const nextFont of fonts) {
      const fontShortcut = `${nextFont.fontName.family}_${nextFont.fontName.style}`;
      if (addedFonts.has(fontShortcut)) continue;
      addedFonts.add(fontShortcut);

      fontsToLoad.push(nextFont.fontName);
    }
  }

  await loadMissingFonts(fontsToLoad);
};

export const loadMissingFonts = async (fontsToLoad: FontName[]) => {
  const fontPromises = fontsToLoad.map((font) =>
    figma.loadFontAsync(font).catch((e) => e)
  );
  const result = await Promise.all(fontPromises);

  if (result.some((singleResult) => !!singleResult)) {
    const allMissingFonts = result
      .map((singleResult, index) => (singleResult ? fontsToLoad[index] : null))
      .filter((singleResult) => !!singleResult) as FontName[];

    const missingSFOrNewYork = allMissingFonts.find(
      (missingFont) =>
        missingFont.family.includes('SF') ||
        missingFont.family.includes('New York')
    );

    const missingFontMessage =
      allMissingFonts.length > 1
        ? `Missing fonts: ${formatFontList(allMissingFonts)}`
        : `Missing font: ${formatFontList(allMissingFonts)}`;

    const missingFontsListMessage =
      allMissingFonts.length > 1
        ? 'These fonts used in your design aren’t installed on your computer. Please install them to enable design reviews.'
        : 'This font used in your design isn’t installed on your computer. Please install it to enable design reviews.';

    if (missingSFOrNewYork) {
      throw new ErrorWithDetails(
        missingFontMessage,
        missingFontsListMessage,
        'https://developer.apple.com/fonts/',
        `Get the ${missingSFOrNewYork.family} font`
      );
    }

    throw new ErrorWithDetails(missingFontMessage, missingFontsListMessage);
  }
};
